import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  GripVertical,
  Plus,
  MessageSquare,
  Calendar,
  ArrowRight,
  Bookmark,
  LayoutDashboard,
  MousePointer2,
} from "lucide-react";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import ProfilePicture1 from "pictures/profile_picture_1_recruiter.jpg";
import ProfilePicture2 from "pictures/profile_picture_2_recruiter.jpg";
import ProfilePicture3 from "pictures/profile_picture_3_recruiter.jpg";
import ProfilePicture4 from "pictures/profile_picture_4_recruiter.png";
import ProfilePicture5 from "pictures/profile_picture_5_recruiter.png";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

const DraggableCandidateCard = ({ candidate, columnId, showNote, autoHighlight }) => {
  const cardRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const isHighlighted = isDragging || autoHighlight;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    return draggable({
      element: el,
      getInitialData: () => ({
        candidateId: candidate.id,
        sourceColumnId: columnId,
      }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [candidate.id, columnId]);

  return (
    <Box
      ref={cardRef}
      sx={{
        bgcolor: "hsl(0, 0%, 100%)",
        borderRadius: "0.75rem",
        p: { xs: "10px", md: "14px" },
        border: isHighlighted
          ? "2px solid hsla(145, 63%, 42%, 0.4)"
          : "1px solid hsl(220, 13%, 91%)",
        position: "relative",
        opacity: isDragging ? 0.5 : 1,
        transform: autoHighlight ? "rotate(-2deg) scale(1.02)" : "none",
        boxShadow: isHighlighted
          ? "0 20px 40px -10px rgba(0,0,0,0.2)"
          : "0 1px 3px rgba(0,0,0,0.06)",
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        transition: "box-shadow 0.2s ease, opacity 0.2s ease, border 0.2s ease",
        "&:hover .bookmark-icon": {
          color: "hsla(220, 10%, 46%, 0.6)",
        },
      }}
    >
      {/* Tag */}
      {candidate?.tag && (
        <Box
          component="span"
          sx={{
            position: "absolute",
            top: "-10px",
            right: "12px",
            fontSize: "10px",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            px: "8px",
            py: "2px",
            borderRadius: "9999px",
            ...(candidate?.tag?.color === "secondary"
              ? {
                bgcolor: "hsla(145, 63%, 42%, 0.15)",
                color: "hsl(145, 63%, 42%)",
              }
              : {
                bgcolor: "#ffedd5",
                color: "#ea580c",
              }),
          }}
        >
          {candidate?.tag?.label}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: "8px", md: "12px" },
        }}
      >
        <Box
          component="img"
          src={candidate?.avatar}
          alt={candidate?.name}
          sx={{
            width: { xs: "32px", md: "36px" },
            height: { xs: "32px", md: "36px" },
            borderRadius: "9999px",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="p"
            sx={{
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              color: "hsl(220, 25%, 12%)",
              lineHeight: 1.25,
              m: 0,
            }}
          >
            {candidate?.name}
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: "11px",
              color: "hsl(220, 10%, 46%)",
              m: 0,
              mt: "2px",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            {candidate?.role}
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: "11px",
              color: "hsla(220, 10%, 46%, 0.7)",
              m: 0,
              mt: "2px",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            {candidate?.subtitle}
          </Box>
        </Box>
        <Box
          className="bookmark-icon"
          sx={{
            color: "hsla(220, 10%, 46%, 0.3)",
            transition: "color 0.3s ease",
            flexShrink: 0,
            mt: "4px",
          }}
        >
          <Bookmark size={14} />
        </Box>
      </Box>

      {/* Note */}
      {showNote && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          sx={{
            mt: "12px",
            bgcolor: "hsla(145, 63%, 42%, 0.05)",
            border: "1px solid hsla(145, 63%, 42%, 0.15)",
            borderRadius: "8px",
            p: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              mb: "4px",
            }}
          >
            <MessageSquare size={10} color="hsl(145, 63%, 42%)" />
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(145, 63%, 42%)",
              }}
            >
              <FormattedMessage id="landing_recruiter.kanban.note_added" />
            </Box>
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: "11px",
              color: "hsl(220, 10%, 46%)",
              lineHeight: 1.625,
              m: 0,
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="landing_recruiter.kanban.note_text" />
          </Box>
        </Box>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mt: { xs: "8px", md: "12px" },
        }}
      >
        <Box
          component="button"
          sx={{
            fontSize: "10px",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            bgcolor: "hsl(178, 93%, 10%)",
            color: "hsl(0, 0%, 98%)",
            px: "10px",
            py: "4px",
            borderRadius: "0.375rem",
            border: "none",
            cursor: "pointer",
          }}
        >
          <FormattedMessage id="landing_recruiter.kanban.btn_view" />
        </Box>
        {columnId === "pending" && (
          <Box
            component="button"
            sx={{
              fontSize: "10px",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              border: "1px solid hsl(220, 13%, 91%)",
              color: "hsl(220, 10%, 46%)",
              bgcolor: "transparent",
              px: "10px",
              py: "4px",
              borderRadius: "0.375rem",
              cursor: "pointer",
            }}
          >
            <FormattedMessage id="landing_recruiter.kanban.btn_send_interview" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const DroppableColumn = ({ col, dropTargetId, children }) => {
  const columnRef = useRef(null);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const el = columnRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      getData: () => ({ columnId: col.id }),
      canDrop: ({ source }) => source.data.sourceColumnId !== col.id,
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [col.id]);

  const highlighted = isOver || dropTargetId === col.id;

  return (
    <Box
      ref={columnRef}
      sx={{
        minWidth: { xs: "200px", md: "240px" },
        flex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: highlighted
          ? "hsla(145, 63%, 42%, 0.05)"
          : "hsla(220, 14%, 96%, 0.5)",
        borderRadius: "0.75rem",
        p: { xs: "10px", md: "12px" },
        transition: "all 0.3s ease",
        outline: highlighted
          ? "2px solid hsla(145, 63%, 42%, 0.4)"
          : "none",
      }}
    >
      {/* Column header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            component="h4"
            sx={{
              fontSize: "0.75rem",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              m: 0,
            }}
          >
            {col.title}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              bgcolor: "hsla(178, 93%, 10%, 0.1)",
              color: "hsl(178, 93%, 10%)",
              px: "6px",
              py: "2px",
              borderRadius: "4px",
            }}
          >
            {col.candidates.length}
          </Box>
        </Box>
      </Box>

      {/* Stats */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          mb: "12px",
          fontSize: "10px",
          color: "hsl(220, 10%, 46%)",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
        }}
      >
        <Box component="span">
          <FormattedMessage id="landing_recruiter.kanban.avg_duration" />{" "}
          <Box
            component="strong"
            sx={{
              color: "hsl(220, 25%, 12%)",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            2.3 J
          </Box>
        </Box>
        <Box component="span">
          <FormattedMessage id="landing_recruiter.kanban.pass_rate" />{" "}
          <Box
            component="strong"
            sx={{
              color: "hsl(220, 25%, 12%)",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            61%
          </Box>
        </Box>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flex: 1,
          overflowY: "auto",
          overflowX: "visible",
          pt: "12px",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}

        {/* Drop zone indicator */}
        {highlighted && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 60 }}
            sx={{
              mt: "10px",
              border: "2px dashed hsla(145, 63%, 42%, 0.4)",
              borderRadius: "0.75rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(145, 63%, 42%)",
              }}
            >
              <FormattedMessage id="landing_recruiter.kanban.drop_here" />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const KanbanShowcase = () => {
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const initialColumns = [
    {
      id: "pending",
      title: intl.formatMessage({ id: "landing_recruiter.kanban.col_pending" }),
      count: 3,
      candidates: [
        {
          id: "c1",
          name: "Marine Debroux",
          role: intl.formatMessage({ id: "landing_recruiter.kanban.role_c1" }),
          subtitle: intl.formatMessage({ id: "landing_recruiter.kanban.subtitle_c1" }),
          avatar: ProfilePicture4,
          tag: { label: intl.formatMessage({ id: "landing_recruiter.kanban.tag_from_squarehub" }), color: "secondary" },
        },
        {
          id: "c2",
          name: "Thomas Laurent",
          role: intl.formatMessage({ id: "landing_recruiter.kanban.role_c2" }),
          subtitle: intl.formatMessage({ id: "landing_recruiter.kanban.subtitle_c2" }),
          avatar: ProfilePicture2,
        },
        {
          id: "c3",
          name: "Sophie Renard",
          role: intl.formatMessage({ id: "landing_recruiter.kanban.role_c3" }),
          subtitle: intl.formatMessage({ id: "landing_recruiter.kanban.subtitle_c3" }),
          avatar: ProfilePicture4,
          tag: { label: intl.formatMessage({ id: "landing_recruiter.kanban.tag_created_by_you" }), color: "warning" },
        },
      ],
    },
    {
      id: "interview",
      title: intl.formatMessage({ id: "landing_recruiter.kanban.col_interview" }),
      count: 2,
      candidates: [
        {
          id: "c4",
          name: "Lucas Bernard",
          role: intl.formatMessage({ id: "landing_recruiter.kanban.role_c4" }),
          subtitle: intl.formatMessage({ id: "landing_recruiter.kanban.subtitle_c4" }),
          avatar: ProfilePicture3,
        },
        {
          id: "c5",
          name: "Emma Dubois",
          role: intl.formatMessage({ id: "landing_recruiter.kanban.role_c5" }),
          subtitle: intl.formatMessage({ id: "landing_recruiter.kanban.subtitle_c5" }),
          avatar: ProfilePicture5,
        },
      ],
    },
    {
      id: "offer",
      title: intl.formatMessage({ id: "landing_recruiter.kanban.col_offer" }),
      count: 1,
      candidates: [
        {
          id: "c6",
          name: "Antoine Mercier",
          role: intl.formatMessage({ id: "landing_recruiter.kanban.role_c6" }),
          subtitle: intl.formatMessage({ id: "landing_recruiter.kanban.subtitle_c6" }),
          avatar: ProfilePicture1,
        },
      ],
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [showNewColumn, setShowNewColumn] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [autoAnimating, setAutoAnimating] = useState(true);
  const [dropTarget, setDropTarget] = useState(null);
  const [autoDragHighlight, setAutoDragHighlight] = useState(null);
  const [cursorPhase, setCursorPhase] = useState("hidden");
  const timersRef = useRef([]);

  const cursorPositions = {
    appear: { x: "18%", y: "45%" },
    grab: { x: "18%", y: "45%" },
    drag: { x: "52%", y: "38%" },
    drop: { x: "52%", y: "38%" },
  };

  // Stop all auto animations permanently when user starts dragging
  const stopAutoAnimation = useCallback(() => {
    if (!autoAnimating) return;
    setAutoAnimating(false);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setAutoDragHighlight(null);
    setDropTarget(null);
    setShowNote(false);
    setShowNewColumn(false);
    setCursorPhase("hidden");
  }, [autoAnimating]);

  // Move a candidate between columns (used by both auto-animation and real DnD)
  const moveCandidate = useCallback((candidateId, sourceColumnId, targetColumnId) => {
    setColumns((prev) => {
      const newCols = prev.map((col) => ({
        ...col,
        candidates: [...col.candidates],
      }));
      const src = newCols.find((c) => c.id === sourceColumnId);
      const dst = newCols.find((c) => c.id === targetColumnId);
      if (!src || !dst) return prev;

      const cardIndex = src.candidates.findIndex((c) => c.id === candidateId);
      if (cardIndex === -1) return prev;

      const [card] = src.candidates.splice(cardIndex, 1);
      src.count = src.candidates.length;
      dst.candidates.push(card);
      dst.count = dst.candidates.length;

      return newCols;
    });
  }, []);

  useEffect(() => {
    const handleDragStart = () => {
      stopAutoAnimation();
    };
    window.addEventListener("dragstart", handleDragStart);
    return () => window.removeEventListener("dragstart", handleDragStart);
  }, [stopAutoAnimation]);

  useEffect(() => {
    let cleanup;
    import("@atlaskit/pragmatic-drag-and-drop/element/adapter").then(
      ({ monitorForElements }) => {
        cleanup = monitorForElements({
          onDrop: ({ source, location }) => {
            const destination = location.current.dropTargets[0];
            if (!destination) return;

            const candidateId = source.data.candidateId;
            const sourceColumnId = source.data.sourceColumnId;
            const targetColumnId = destination.data.columnId;

            if (sourceColumnId !== targetColumnId) {
              moveCandidate(candidateId, sourceColumnId, targetColumnId);
            }
          },
        });
      }
    );

    return () => {
      if (cleanup) cleanup();
    };
  }, [moveCandidate]);

  useEffect(() => {
    if (!autoAnimating) return;

    const phases = [
      { delay: 1500, action: () => setCursorPhase("appear") },
      { delay: 2000, action: () => { setCursorPhase("grab"); setAutoDragHighlight("c1"); } },
      { delay: 3200, action: () => { setCursorPhase("drag"); setDropTarget("interview"); } },
      {
        delay: 4200,
        action: () => {
          setCursorPhase("drop");
          moveCandidate("c1", "pending", "interview");
          setAutoDragHighlight(null);
          setDropTarget(null);
        },
      },
      { delay: 5000, action: () => setCursorPhase("hidden") },
      { delay: 6000, action: () => setShowNote(true) },
      { delay: 8500, action: () => setShowNote(false) },
      { delay: 10000, action: () => setShowNewColumn(true) },
      {
        delay: 13000,
        action: () => setShowNewColumn(false),
      },
      {
        delay: 14500,
        action: () => {
          setColumns(initialColumns);
          setCursorPhase("hidden");
          setAnimationPhase((p) => p + 1);
        },
      },
    ];

    const timers = phases.map((p) => setTimeout(p.action, p.delay));
    timersRef.current = timers;
    return () => timers.forEach(clearTimeout);
  }, [animationPhase, autoAnimating, moveCandidate]);

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: "40px", md: "56px" },
        pb: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(178, 93%, 10%)",
        color: "hsl(0, 0%, 98%)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ maxWidth: "1152px", mx: "auto" }}>
        {/* Header */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "24px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              px: "16px",
              py: "6px",
              borderRadius: "9999px",
              bgcolor: "hsla(145, 63%, 42%, 0.2)",
              border: "1px solid hsla(145, 63%, 42%, 0.3)",
              color: "hsl(145, 63%, 42%)",
              fontSize: "0.75rem",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: "16px",
            }}
          >
            <LayoutDashboard size={14} />
            <FormattedMessage id="landing_recruiter.kanban.badge" />
          </Box>
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(0, 0%, 98%)",
              letterSpacing: "-0.025em",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage id="landing_recruiter.kanban.title" />
          </Box>
          <Box
            component="p"
            sx={{
              color: "hsla(0, 0%, 98%, 0.7)",
              fontSize: "1.125rem",
              maxWidth: "672px",
              mx: "auto",
              textAlign: "center",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="landing_recruiter.kanban.subtitle" />
          </Box>
        </Box>

        {/* Feature pills */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "8px",
            mb: "40px",
          }}
        >
          {[
            { icon: GripVertical, label: intl.formatMessage({ id: "landing_recruiter.kanban.feature_drag_drop" }) },
            { icon: Plus, label: intl.formatMessage({ id: "landing_recruiter.kanban.feature_custom_columns" }) },
            { icon: MessageSquare, label: intl.formatMessage({ id: "landing_recruiter.kanban.feature_notes" }) },
            { icon: Calendar, label: intl.formatMessage({ id: "landing_recruiter.kanban.feature_schedule" }) },
          ].map((feature) => (
            <Box
              key={feature.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bgcolor: "hsl(0, 0%, 100%)",
                border: "1px solid hsl(220, 13%, 91%)",
                borderRadius: "9999px",
                px: "16px",
                py: "8px",
                fontSize: "0.875rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(220, 25%, 12%)",
              }}
            >
              <feature.icon size={14} color="hsl(145, 63%, 42%)" />
              {feature.label}
            </Box>
          ))}
        </Box>

        {/* Kanban Board */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{
            bgcolor: "hsl(0, 0%, 100%)",
            border: "1px solid hsl(220, 13%, 91%)",
            borderRadius: "1rem",
            p: { xs: "16px", md: "24px" },
            boxShadow:
              "0 10px 15px -3px hsla(220, 25%, 12%, 0.03), 0 4px 6px -4px hsla(220, 25%, 12%, 0.03)",
            position: "relative",
          }}
        >
          {/* Animated cursor */}
          <AnimatePresence>
            {cursorPhase !== "hidden" && (
              <Box
                component={motion.div}
                initial={{
                  opacity: 0,
                  left: cursorPositions.appear.x,
                  top: cursorPositions.appear.y,
                  scale: 0.5,
                }}
                animate={{
                  opacity: 1,
                  left: cursorPositions[cursorPhase]?.x ?? cursorPositions.appear.x,
                  top: cursorPositions[cursorPhase]?.y ?? cursorPositions.appear.y,
                  scale: cursorPhase === "grab" || cursorPhase === "drag" ? 0.9 : 1,
                }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
                sx={{
                  position: "absolute",
                  zIndex: 100,
                  pointerEvents: "none",
                  transform: "translate(-4px, -4px)",
                  display: { xs: "none", md: "block" },
                }}
              >
                {cursorPhase === "grab" || cursorPhase === "drag" ? (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 14.5V7.5C8 6.67 8.67 6 9.5 6S11 6.67 11 7.5V4.5C11 3.67 11.67 3 12.5 3S14 3.67 14 4.5V7.5C14 6.67 14.67 6 15.5 6S17 6.67 17 7.5V9.5C17 8.67 17.67 8 18.5 8S20 8.67 20 9.5V16.5C20 19.54 17.54 22 14.5 22H13C10.24 22 8 19.76 8 17V14.5Z" fill="hsl(145, 63%, 42%)" stroke="hsl(220, 25%, 12%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11 7.5V12M14 7V12M17 9.5V12" stroke="hsl(220, 25%, 12%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4"/>
                  </svg>
                ) : (
                  <MousePointer2
                    size={28}
                    color="hsl(220, 25%, 12%)"
                    fill="hsl(0, 0%, 100%)"
                    strokeWidth={2}
                    style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }}
                  />
                )}
              </Box>
            )}
          </AnimatePresence>

          {/* Top bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "20px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                component="h3"
                sx={{
                  fontSize: "0.875rem",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  color: "hsl(220, 25%, 12%)",
                  m: 0,
                }}
              >
                <FormattedMessage id="landing_recruiter.kanban.board_title" />
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "10px",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  bgcolor: "hsl(145, 63%, 42%)",
                  color: "hsl(0, 0%, 100%)",
                  px: "8px",
                  py: "2px",
                  borderRadius: "9999px",
                  display: { xs: "none", sm: "inline-block" },
                }}
              >
                <FormattedMessage id="landing_recruiter.kanban.steps_count" />
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box
                component="button"
                sx={{
                  fontSize: "11px",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  border: "1px solid hsl(220, 13%, 91%)",
                  color: "hsl(220, 10%, 46%)",
                  bgcolor: "transparent",
                  px: "12px",
                  py: "6px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    bgcolor: "hsl(220, 14%, 96%)",
                  },
                }}
              >
                <FormattedMessage id="landing_recruiter.kanban.manage_board" />
              </Box>
              <Box
                component="button"
                sx={{
                  fontSize: "11px",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  bgcolor: "hsl(145, 63%, 42%)",
                  color: "hsl(0, 0%, 100%)",
                  border: "none",
                  px: "12px",
                  py: "6px",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <FormattedMessage id="landing_recruiter.kanban.add_candidate" />
              </Box>
            </Box>
          </Box>

          {/* Columns */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: "8px", md: "12px" },
              overflowX: "auto",
              overflowY: "hidden",
              pb: "8px",
              height: { xs: "520px", md: "480px" },
              alignItems: "stretch",
            }}
          >
            <AnimatePresence>
              {columns.map((col) => (
                <DroppableColumn
                  key={col.id}
                  col={col}
                  dropTargetId={dropTarget}
                >
                  {col.candidates.map((candidate) => (
                    <DraggableCandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      columnId={col.id}
                      showNote={showNote && candidate.id === "c4"}
                      autoHighlight={autoDragHighlight === candidate.id}
                    />
                  ))}
                </DroppableColumn>
              ))}

              {/* New column animation (auto-animation only) */}
              {showNewColumn && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 240 }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 25,
                  }}
                  sx={{
                    minWidth: "240px",
                    height: "100%",
                    alignSelf: "stretch",
                    flexShrink: 0,
                    bgcolor: "hsla(145, 63%, 42%, 0.05)",
                    border: "2px dashed hsla(145, 63%, 42%, 0.3)",
                    borderRadius: "0.75rem",
                    p: "12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "9999px",
                      bgcolor: "hsla(145, 63%, 42%, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Plus size={16} color="hsl(145, 63%, 42%)" />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      color: "hsl(145, 63%, 42%)",
                    }}
                  >
                    <FormattedMessage id="landing_recruiter.kanban.new_column" />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "10px",
                      color: "hsl(220, 10%, 46%)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    }}
                  >
                    <FormattedMessage id="landing_recruiter.kanban.technical_test" />
                  </Box>
                </Box>
              )}
            </AnimatePresence>
          </Box>
        </Box>

        {/* Bottom CTA */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          sx={{ mt: "32px", textAlign: "center" }}
        >
          <Button
            component="a"
            href={`/${i18nLocale}/recruiters/sign_up`}
            sx={{
              bgcolor: "hsl(153 68% 25%)",
              color: "white",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: "0.875rem",
              px: "1.5rem",
              py: "0.875rem",
              borderRadius: "0.75rem",
              textTransform: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              textDecoration: "none",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
                textDecoration: "none",
              },
            }}
          >
            <FormattedMessage id="landing_recruiter.kanban.cta_discover_ats" /> <ArrowRight size={14} className="arrow-icon" />
          </Button>
        </Box>

        {/* Demo CTA banner */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{
            mt: "48px",
            position: "relative",
            overflow: "hidden",
            borderRadius: "1rem",
            border: "1px solid hsla(145, 63%, 42%, 0.3)",
            background:
              "linear-gradient(to right, hsla(145, 63%, 42%, 0.15), hsla(145, 63%, 42%, 0.05), hsla(145, 63%, 42%, 0.1))",
            p: { xs: "32px", md: "48px" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at top right, hsla(145, 63%, 42%, 0.15), transparent 60%)",
            }}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: "24px",
            }}
          >
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Box
                component="h3"
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.875rem" },
                  fontFamily: "Segoe UI Black, system-ui, sans-serif",
                  color: "hsl(0, 0%, 98%)",
                  mb: "8px",
                  mt: 0,
                }}
              >
                <FormattedMessage id="landing_recruiter.kanban.demo_title" />
              </Box>
              <Box
                component="p"
                sx={{
                  color: "hsla(0, 0%, 98%, 0.6)",
                  fontSize: "0.875rem",
                  maxWidth: "448px",
                  m: 0,
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                <FormattedMessage id="landing_recruiter.kanban.demo_subtitle" />
              </Box>
            </Box>
            <Button
              component="a"
              href={
                i18nLocale === "fr-fr"
                  ? "https://meetings-eu1.hubspot.com/meetings/felix-dessirier"
                  : i18nLocale === "nl"
                    ? "https://meetings-eu1.hubspot.com/meetings/jules-fadie"
                    : "https://meetings-eu1.hubspot.com/meetings/sebastien-del-marmol"
              }
              sx={{
                bgcolor: "hsl(153 68% 25%)",
                color: "white",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                fontWeight: 700,
                fontSize: "0.875rem",
                px: "1.5rem",
                py: "0.875rem",
                borderRadius: "0.75rem",
                textTransform: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                flexShrink: 0,
                textDecoration: "none",
                "&:hover": {
                  bgcolor: "hsl(153 68% 25% / 0.9)",
                  textDecoration: "none",
                },
              }}
            >
              <FormattedMessage id="landing_recruiter.kanban.demo_cta" /> <ArrowRight size={14} className="arrow-icon" />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default KanbanShowcase;
